import type { TranslationDict } from "../types";

export const es = {
  meta: {
    homeTitle: "Meridian Motors — Vehículos de lujo y alto rendimiento",
    homeDescription:
      "Un showroom privado para conductores exigentes. Vehículos de lujo, rendimiento y eléctricos seleccionados a mano, con entrega concierge y financiamiento a medida.",
    catalogTitle: "Inventario — Meridian Motors",
    catalogDescription:
      "Explora nuestro inventario completo de vehículos de lujo, rendimiento y eléctricos certificados. Filtra por marca, carrocería, precio, kilometraje y más.",
    notFoundTitle: "Página no encontrada — Meridian Motors",
  },
  nav: {
    home: "Inicio",
    inventory: "Inventario",
    financing: "Financiamiento",
    about: "Nosotros",
    contact: "Contacto",
    bookViewing: "Agendar visita",
    toggleMenu: "Abrir menú",
    language: "Idioma",
  },
  footer: {
    tagline: "Automoción de lujo curada",
    blurb:
      "Un showroom privado para el conductor exigente. Cada vehículo de nuestro inventario está seleccionado a mano, certificado mecánicamente y entregado con la atención que merece.",
    showroom: "Showroom",
    navigate: "Navegar",
    links: ["Inventario", "Financiamiento", "Trade-in", "Garantía", "Concierge", "Prensa"] as const,
    rights: "Todos los derechos reservados.",
    privacy: "Privacidad",
    terms: "Términos",
    accessibility: "Accesibilidad",
    social: "Enlace social",
  },
  hero: {
    eyebrow: "Colección de verano · 2024",
    titleLine1: "La carretera,",
    titleLine2: "reimaginada.",
    copy: "Un showroom privado de vehículos de lujo y alto rendimiento seleccionados a mano. Entrega concierge, financiamiento a medida y un equipo de postventa que cuida tu auto como se merece.",
    explore: "Explorar inventario",
    bookPrivate: "Agendar visita privada",
    stats: [
      { k: "12+", v: "Años curando" },
      { k: "340", v: "Autos entregados" },
      { k: "4.9★", v: "Valoración" },
    ] as const,
  },
  featured: {
    eyebrow: "Inventario destacado",
    title: "Llegadas de esta semana",
    copy: "Una selección rotativa de vehículos certificados de ocasión y nuevos de fábrica. Cada listado está inspeccionado, documentado y listo para entregar.",
    viewAll: "Ver los {count}+ vehículos",
  },
  brands: {
    eyebrow: "Alianzas autorizadas",
    title: "Marcas que representamos",
  },
  promotions: {
    eyebrow: "Promociones vigentes",
    title: "Ofertas exclusivas",
    items: [
      {
        title: "Concierge de temporada",
        body: "Detailing y entrega sin costo en un radio de 160 km en compras superiores a $80k.",
        cta: "Ver oferta",
      },
      {
        title: "Bono por trade-in",
        body: "Recibe hasta $5,000 por encima del valor tasado al canjear por un vehículo certificado.",
        cta: "Solicitar tasación",
      },
      {
        title: "Garantía extendida",
        body: "Dos años de mantenimiento concierge incluidos en cada compra certificada de ocasión.",
        cta: "Más información",
      },
    ] as const,
  },
  financing: {
    eyebrow: "Financiamiento y leasing",
    title: "Propiedad en tus términos.",
    copy: "Ya sea compra, leasing o una estructura híbrida, nuestro escritorio financiero interno arma un plan a la medida de cómo conduces y mantienes tus vehículos.",
    perks: [
      {
        title: "Tasas a medida",
        body: "Desde 4.9% APR a través de nuestros socios bancarios privados.",
      },
      {
        title: "Aprobado en horas",
        body: "Preaprobación suave que no afecta tu historial crediticio.",
      },
      {
        title: "Plazos flexibles",
        body: "De 24 a 84 meses, con opciones balloon y leasing.",
      },
    ] as const,
    estimatorTitle: "Estimador de pagos",
    estimatorHint: "Solo ilustrativo. Las tasas finales dependen del perfil crediticio.",
    vehiclePrice: "Precio del vehículo",
    downPayment: "Inicial",
    term: "Plazo",
    termUnit: "{n} meses",
    apr: "APR",
    estimatedMonthly: "Cuota estimada",
    getPreApproved: "Preaprobarme",
  },
  testimonials: {
    eyebrow: "Opiniones de clientes",
    title: "La confianza de quien sabe conducir.",
    items: [
      {
        name: "Jordan Reyes",
        role: "Fundador, Halcyon Studio",
        body: "Comprar aquí se sintió más como unirse a un club privado que a un concesionario. Mi Vantage llegó un domingo, impecable.",
      },
      {
        name: "Priya Anand",
        role: "Socia gerenta",
        body: "Meridian encontró una especificación concreta del RS7 en menos de una semana. Su red es inigualable.",
      },
      {
        name: "Marcus Chen",
        role: "Ejecutivo tech",
        body: "He canjeado tres autos con ellos. Cada tasación ha sido justa y cada seguimiento, personal.",
      },
    ] as const,
  },
  about: {
    eyebrow: "Quiénes somos",
    title: "Un concesionario más silencioso.",
    p1: "Meridian Motors nació en 2012 con una premisa simple: comprar un auto de alto rendimiento debe sentirse tan cuidado como la máquina misma. Mantemos el inventario reducido, el showroom con cita y al equipo obsesionado con el detalle.",
    p2: "Cada vehículo que vendemos pasa una inspección mecánica y cosmética de 220 puntos, viene con historial de servicio completo y se entrega con dos años de mantenimiento concierge incluido.",
    stats: [
      ["220", "Puntos de inspección"],
      ["48h", "Entrega"],
      ["12 años", "En el mercado"],
    ] as const,
  },
  faq: {
    eyebrow: "Preguntas frecuentes",
    title: "Respuestas, antes de que preguntes.",
    copy: "Preguntas habituales de clientes nuevos y recurrentes. ¿No ves la tuya? Nuestro equipo concierge responde en menos de una hora.",
    items: [
      {
        q: "¿Ofrecen entrega a nivel nacional?",
        a: "Sí. Entregamos con transporte cerrado y climatizado a cualquier punto de Venezuela en 5–7 días hábiles.",
      },
      {
        q: "¿Puedo entregar mi vehículo actual?",
        a: "Por supuesto. Agenda una tasación y te daremos una oferta escrita competitiva, normalmente en 24 horas, aplicable a cualquier compra del inventario.",
      },
      {
        q: "¿Cómo funciona el financiamiento?",
        a: "Trabajamos con una red de bancos privados y captivos para armar un plan personalizado. La preaprobación suave no afecta tu crédito.",
      },
      {
        q: "¿Los vehículos se inspeccionan antes de la venta?",
        a: "Cada unidad pasa una inspección mecánica y cosmética de 220 puntos. Lo que esté fuera de especificación de fábrica se repara a estándar OEM antes de listarse.",
      },
      {
        q: "¿Aceptan criptomonedas o transferencia?",
        a: "Aceptamos fondos certificados, transferencias bancarias y criptomonedas selectas a través de nuestro socio de escrow. Consulta con tu asesor.",
      },
    ] as const,
  },
  contact: {
    eyebrow: "Visita el showroom",
    title: "Hablemos de tu próximo auto.",
    copy: "Agenda una visita privada, pide un video walkaround o inicia una conversación sobre una especificación concreta — buscamos a nivel global.",
    showroom: "Showroom",
    direct: "Línea directa",
    hours: "Lun–Sáb, 9am–7pm",
    concierge: "Concierge",
    mapTitle: "Mapa de ubicación de Meridian Motors",
    formTitle: "Enviar consulta",
    formHint: "Respondemos en menos de una hora hábil, Lun–Sáb.",
    fullName: "Nombre completo",
    email: "Correo",
    phone: "Teléfono",
    interestedIn: "Interesado en",
    anyVehicle: "Cualquier vehículo",
    message: "Mensaje",
    messagePlaceholder: "Cuéntanos qué buscas...",
    namePlaceholder: "Alex Morgan",
    emailPlaceholder: "tu@empresa.com",
    phonePlaceholder: "+58 412 000-0000",
    send: "Enviar consulta",
    sending: "Enviando...",
    sent: "Mensaje recibido ✓",
    privacyNote: "Al enviar aceptas nuestra política de privacidad.",
    sendError: "No se pudo enviar la consulta",
  },
  catalog: {
    eyebrow: "La colección",
    title: "Inventario",
    subtitleLoaded:
      "{count} vehículos en stock. Filtra, compara y reserva en línea — o agenda una visita privada en el showroom.",
    subtitleIdle:
      "Filtra, compara y reserva en línea — o agenda una visita privada en el showroom.",
    filters: "Filtros",
    reset: "Restablecer",
    searchPlaceholder: "Buscar modelo...",
    brand: "Marca",
    bodyStyle: "Carrocería",
    fuel: "Combustible",
    transmission: "Transmisión",
    condition: "Condición",
    maxPrice: "Precio máx.",
    inStockOnly: "Solo en stock",
    all: "Todos",
    hideFilters: "Ocultar filtros",
    showFilters: "Filtros",
    loading: "Cargando…",
    error: "Error",
    results: "{count} resultados",
    sort: "Ordenar",
    sorts: {
      featured: "Destacados",
      "price-asc": "Precio · Menor a mayor",
      "price-desc": "Precio · Mayor a menor",
      "year-desc": "Año · Más nuevos",
      "mileage-asc": "Kilometraje · Menor",
      "power-desc": "Potencia · Mayor",
    } as const,
    loadError: "No se pudo cargar el inventario.",
    retry: "Reintentar",
    emptyInventory: "No hay vehículos en inventario.",
    emptyInventoryHint: "Vuelve pronto o contacta al showroom.",
    noMatch: "Ningún vehículo coincide con esos filtros.",
    noMatchHint: "Amplía la búsqueda o restablece los filtros.",
    resetFilters: "Restablecer filtros",
  },
  vehicle: {
    orLease: "o leasing",
    reserved: "Reservado",
    inStock: "En stock",
    price: "Precio",
    fromMo: "Desde / mes · 72",
    specifications: "Especificaciones",
    features: "Equipamiento",
    year: "Año",
    body: "Carrocería",
    condition: "Condición",
    mileage: "Kilometraje",
    power: "Potencia",
    acceleration: "0-100 km/h",
    topSpeed: "Vel. máxima",
    drivetrain: "Tracción",
    transmission: "Transmisión",
    fuel: "Combustible",
    exterior: "Exterior",
    availability: "Disponibilidad",
    whatsapp: "WhatsApp",
    call: "Llamar",
    email: "Correo",
    reserveNow: "Reservar ahora",
    brochure: "Folleto",
    close: "Cerrar",
    prevImage: "Imagen anterior",
    nextImage: "Imagen siguiente",
  },
  notFound: {
    title: "Página no encontrada",
    copy: "La página que buscas no existe o fue movida.",
    goHome: "Ir al inicio",
  },
  admin: {
    meta: {
      title: "Consola Admin — Meridian Motors",
      description: "Consola de administración interna de Meridian Motors.",
    },
    console: "Consola Admin",
    collapseSidebar: "Contraer barra lateral",
    menu: "Menú",
    conciergeAi: "Concierge AI",
    conciergeCopy: "Redacta listados, responde leads y etiqueta inventario automáticamente.",
    enablePreview: "Activar vista previa",
    searchPlaceholder: "Buscar vehículos, leads, VIN…",
    toggleTheme: "Cambiar tema",
    recentActivity: "Actividad reciente",
    minutesAgo: "hace unos minutos",
    notifications: [
      "Nuevo lead — Elena Kovacs sobre Audi RS7",
      "Prueba de manejo agendada — BMW X7 M60i",
      "Listado Polestar 4 publicado",
      "Puntuación SEO mejorada: +6 pts esta semana",
    ] as const,
    roleSalesDirector: "Directora de ventas",
    myAccount: "Mi cuenta",
    profile: "Perfil",
    preferences: "Preferencias",
    billing: "Facturación",
    returnToSite: "Volver al sitio",
    crumbs: {
      admin: "Admin",
      vehicles: "Vehículos",
      leads: "Leads y CRM",
      media: "Biblioteca de medios",
      seo: "SEO y Analytics",
      analytics: "Insights de inventario",
      settings: "Configuración",
      new: "Nuevo",
    },
    nav: {
      dashboard: "Dashboard",
      vehicles: "Vehículos",
      leads: "Leads y CRM",
      media: "Biblioteca de medios",
      seo: "SEO y Analytics",
      analytics: "Insights de inventario",
      settings: "Configuración",
    },
    common: {
      somethingWrong: "Algo salió mal",
      retry: "Reintentar",
      noData: "No hay datos disponibles.",
      vsLastMonth: "vs mes anterior",
      previous: "Anterior",
      next: "Siguiente",
      more: "Más",
      yes: "Sí",
      no: "No",
      justNow: "Justo ahora",
      available: "Disponible",
      reserved: "Reservado",
      featured: "Destacado",
      all: "Todos",
      edit: "Editar",
      duplicate: "Duplicar",
      feature: "Destacar",
      unfeature: "Quitar destacado",
      unpublish: "Despublicar",
      archive: "Archivar",
      reply: "Responder",
    },
    dashboard: {
      title: "Dashboard ejecutivo",
      description: "Vista en tiempo real del inventario, engagement y salud del pipeline.",
      newListing: "Nuevo listado",
      totalInventory: "Inventario total",
      availableNow: "Disponibles ahora",
      reservedHint: "{count} reservados",
      inventoryValue: "Valor del inventario",
      avgPrice: "Precio promedio",
      totalLeads: "Leads totales (30d)",
      unreadInquiries: "Consultas sin leer",
      needsTriage: "requiere triaje",
      conversionRate: "Tasa de conversión",
      featuredVehicles: "Vehículos destacados",
      trafficTitle: "Tráfico y generación de leads",
      trafficSubtitle: "Visitas diarias vs consultas calificadas — últimos 30 días",
      sourcesTitle: "Fuentes de tráfico",
      sourcesSubtitle: "Participación de sesiones",
      revenueTitle: "Ingresos por mes",
      revenueSubtitle: "Ventas brutas — últimos 12 meses",
      brandMix: "Mix de marcas",
      brandMixSubtitle: "Inventario por fabricante",
      funnelTitle: "Embudo de ventas",
      funnelSubtitle: "Recorrido de 30 días",
      engagedTitle: "Vehículos más consultados",
      engagedSubtitle: "Vistas y consultas",
      leadsCount: "{count} leads",
    },
    vehicles: {
      title: "Inventario de vehículos",
      description: "Gestiona listados, estado de publicación, galería y financiamiento.",
      newVehicle: "Nuevo vehículo",
      empty: "No hay vehículos en inventario.",
      search: "Buscar por marca, modelo, VIN…",
      brand: "Marca",
      body: "Carrocería",
      status: "Estado",
      allBrands: "Todas las marcas",
      allBodies: "Todas las carrocerías",
      allStatuses: "Todos los estados",
      selected: "{count} seleccionados",
      feature: "Destacar",
      unpublish: "Despublicar",
      archive: "Archivar",
      archived: "Vehículo archivado",
      archivedDesc: "Puedes restaurarlo desde el archivo.",
      archiveFail: "No se pudo archivar el vehículo",
      duplicated: "Vehículo duplicado",
      duplicateFail: "No se pudo duplicar el vehículo",
      archivedMany: "Vehículos archivados",
      archiveManyFail: "Error al archivar",
      colVehicle: "Vehículo",
      colStock: "Stock",
      colMileage: "Kilometraje",
      colPrice: "Precio",
      colStatus: "Estado",
      colViews: "Vistas",
      noMatch: "Ningún vehículo coincide con tus filtros",
      showing: "Mostrando {filtered} de {total} vehículos",
    },
    leads: {
      title: "Leads y CRM",
      description: "Todas las consultas del sitio en un pipeline inteligente.",
      empty: "Aún no hay leads.",
      table: "tabla",
      board: "tablero",
      search: "Buscar leads por nombre o correo…",
      colLead: "Lead",
      colVehicle: "Vehículo",
      colSource: "Fuente",
      colStatus: "Estado",
      colOwner: "Responsable",
      colValue: "Valor",
      movedTo: "Lead movido a {status}",
      updateFail: "No se pudo actualizar el lead",
      email: "Correo",
      phone: "Teléfono",
      owner: "Responsable",
      created: "Creado",
      source: "Fuente",
      priority: "Prioridad",
      message: "Mensaje",
      activity: "Actividad",
      activityCreated: "Lead creado desde el formulario de contacto",
      activityAssigned: "Asignado automáticamente a {owner}",
      activityOpened: "Correo abierto",
      time2h: "hace 2h",
      time1h: "hace 1h",
      time45m: "hace 45m",
      scheduleTestDrive: "Agendar prueba de manejo",
      statuses: {
        New: "Nuevo",
        Contacted: "Contactado",
        Qualified: "Calificado",
        "Test Drive": "Prueba de manejo",
        Won: "Ganado",
        Lost: "Perdido",
      },
    },
    media: {
      title: "Biblioteca de medios",
      description: "Cada imagen, video y folleto de la plataforma — organizado y buscable.",
      upload: "Subir archivos",
      empty: "Aún no hay archivos multimedia.",
      totalAssets: "Archivos totales",
      storageUsed: "Almacenamiento usado",
      folders: "Carpetas",
    },
    seo: {
      title: "SEO y Analytics",
      description: "Salud de metadatos, datos estructurados, rankings y Core Web Vitals.",
      indexedPages: "Páginas indexadas",
      brokenLinks: "Enlaces rotos",
      sitemap: "Sitemap",
      structuredData: "Datos estructurados",
      keywordRankings: "Rankings de keywords",
      keyword: "Keyword",
      position: "Posición",
      volume: "Volumen",
      change: "Cambio",
      coreWebVitals: "Core Web Vitals",
      valid: "Válido",
      ok: "OK",
    },
    analytics: {
      title: "Insights de inventario",
      description: "Analítica profunda de composición, rotación y engagement del inventario.",
      daysOnMarket: "Días en mercado",
      bodyStyle: "Distribución por carrocería",
      monthlyAdditions: "Altas mensuales",
    },
    settings: {
      title: "Configuración",
      description: "Identidad de marca, contacto, integraciones y acceso del equipo.",
      companyProfile: "Perfil de la empresa",
      dealershipName: "Nombre del concesionario",
      tagline: "Eslogan",
      about: "Acerca de",
      contact: "Contacto",
      phone: "Teléfono",
      email: "Correo",
      address: "Dirección",
      preferences: "Preferencias",
      financingCalc: "Calculadora de financiamiento pública",
      financingCalcDesc: "Mostrar estimador interactivo de cuota mensual.",
      autoAssign: "Asignación automática de leads",
      autoAssignDesc: "Distribuir nuevas consultas al equipo de ventas en round-robin.",
      whatsappTracking: "Tracking de clics de WhatsApp",
      whatsappTrackingDesc: "Registrar toques del CTA de WhatsApp en analytics.",
      maintenance: "Modo mantenimiento",
      maintenanceDesc: "Ocultar la tienda y mostrar una página de próximamente.",
      team: "Equipo",
      adminBadge: "Admin",
      taglineValue: "Un showroom privado para el conductor exigente.",
      aboutValue:
        "Vehículos de lujo, rendimiento y eléctricos con entrega concierge.",
      teamMembers: [
        "Ava Marchetti — Directora de ventas",
        "Jonas Weller — Asesor senior",
        "Priya Ramesh — Concierge",
        "Marcus Vale — Jefe de detailing",
      ] as const,
    },
    editor: {
      back: "Volver al inventario",
      notFound: "Vehículo no encontrado.",
      newTitle: "Nuevo listado de vehículo",
      newDescription: "Crea un nuevo listado — todos los campos se pueden editar después.",
      editDescription: "Actualiza detalles, galería, precios y estado de publicación.",
      autofill: "Autocompletar con IA",
      createListing: "Crear listado",
      saveChanges: "Guardar cambios",
      created: "Vehículo creado",
      updated: "Vehículo actualizado",
      savedDesc: "Cambios guardados en el catálogo de inventario.",
      saveFail: "No se pudo guardar el vehículo",
      sections: {
        general: "General",
        pricing: "Precios",
        specs: "Especificaciones",
        features: "Equipamiento",
        gallery: "Galería",
        seo: "SEO",
        status: "Publicación",
      },
      brand: "Marca",
      model: "Modelo",
      trim: "Versión",
      year: "Año",
      vin: "VIN",
      stockNumber: "Número de stock",
      slug: "Slug",
      exteriorColor: "Color exterior",
      interiorColor: "Color interior",
      description: "Descripción",
      descriptionPlaceholder:
        "Escribe una narrativa convincente sobre diseño, rendimiento y procedencia…",
      listPrice: "Precio de lista (USD)",
      promoPrice: "Precio promocional (USD)",
      optional: "Opcional",
      downPayment: "Inicial %",
      apr: "APR %",
      term: "Plazo (meses)",
      warranty: "Garantía (años)",
      estimatedMonthly: "Cuota estimada",
      perMonth: "/mes",
      body: "Carrocería",
      fuel: "Combustible",
      transmission: "Transmisión",
      drivetrain: "Tracción",
      horsepower: "Caballos de fuerza",
      torque: "Torque (lb-ft)",
      acceleration: "0–100 km/h (s)",
      topSpeed: "Vel. máxima (km/h)",
      mileage: "Kilometraje (mi)",
      doors: "Puertas",
      seats: "Asientos",
      condition: "Condición",
      conditionNew: "Nuevo",
      conditionCertified: "Certificado",
      conditionUsed: "Usado",
      addFeaturePlaceholder: "Agregar equipamiento — ej. Bang & Olufsen 3D sound",
      add: "Agregar",
      noFeatures: "Sin equipamiento aún — escribe arriba.",
      dropImages: "Suelta imágenes o haz clic para subir",
      dropHint: "Hasta 20 fotos · JPG, PNG, WebP · optimización automática",
      uploadInfo: "La subida de imágenes requiere un backend de almacenamiento.",
      cover: "Portada",
      seoTitle: "Título SEO",
      metaDescription: "Meta descripción",
      canonical: "URL canónica",
      keywords: "Keywords",
      searchPreview: "Vista previa de búsqueda",
      forSale: "en venta | Meridian Motors",
      published: "Publicado",
      publishedDesc: "El listado es visible públicamente en la tienda.",
      featuredHome: "Destacado en inicio",
      featuredHomeDesc: "Resaltado en colecciones y grillas destacadas.",
      availablePurchase: "Disponible para compra",
      availablePurchaseDesc: "Desmarca para reservar o marcar como vendido pendiente de contrato.",
      allowFinancing: "Permitir calculadora de financiamiento",
      allowFinancingDesc: "Mostrar estimador interactivo de cuota en la página de detalle.",
      publicationStatus: "Estado de publicación",
      status: "Estado",
      live: "En vivo",
      draft: "Borrador",
      featuredLabel: "Destacado",
      lastEdit: "Última edición",
      savePublish: "Guardar y publicar",
      completeness: "Completitud",
      completenessHint: "86% — agrega fotos interiores y términos de garantía para llegar al 100%.",
    },
  },
} satisfies TranslationDict;
